import React, { useCallback, useState } from 'react';

import { css } from '@emotion/react';
import {
  Heading,
  HStack,
  Primitive,
  Select,
  VStack,
} from '@rtkwlf/fenrir-react';
import type { Ticket, TicketWithComments } from '@rtkwlf/rendall';
import type { QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';

import { TicketActionForm } from './ticket-action-form/ticket-action-form';
import { TicketRuleTypeSelect } from './ticket-rule-type-select/ticket-rule-type-select';
import type { TicketRuleTypeSelectValue } from './ticket-rule-type-select/use-ticket-rule-type-select-options/use-ticket-rule-type-select-options';
import { CloseAction } from './ticketAction.constants';
import { updateTicketList } from './ticketAction.utils';
import { useTicketActions } from './use-ticket-actions/use-ticket-actions';

export type TicketActionProps = {
  ticket: TicketWithComments;
  ticketListQueryKey?: QueryKey;
};

export const TicketAction: React.FC<TicketActionProps> = ({
  ticket,
  ticketListQueryKey,
}) => {
  const { t } = useTranslation('ticket');
  const organization = useSelectedOrganization();
  const queryClient = useQueryClient();
  const ticketActions = useTicketActions({ ticket });
  const [actionType, setActionType] = useState(ticketActions[0]?.value);
  const [ruleType, setRuleType] = useState<TicketRuleTypeSelectValue>();
  const onUpdateSuccess = useCallback(
    (data: TicketWithComments) => {
      if (ticketListQueryKey) {
        updateTicketList({
          queryClient,
          queryKey: ticketListQueryKey,
          ticket: data as Ticket,
        });
      }
      queryClient.setQueryData(
        ticketKeys.detail(organization.id, `${ticket.id}`),
        data,
      );
    },
    [organization.id, queryClient, ticket.id, ticketListQueryKey],
  );

  if (!ticketActions.length) return null;

  return (
    <Primitive.div width="full" marginTop="medium">
      <HStack width="full" marginBottom="medium">
        <Heading styledAs="heading3">{t('action.title')}</Heading>
        <Primitive.div css={css({ minWidth: '20rem' })}>
          <Select
            value={actionType}
            appearance="neutral"
            options={ticketActions}
            isClearable={false}
            onValueChange={setActionType as (value: string) => void}
          />
        </Primitive.div>
      </HStack>

      {actionType === CloseAction.ManageAlertConfiguration && (
        <VStack marginTop="large">
          <Heading level="4">{t('createAlertConfiguration')}</Heading>

          <TicketRuleTypeSelect value={ruleType} onChange={setRuleType} />
        </VStack>
      )}

      <TicketActionForm
        actionType={actionType}
        ruleType={ruleType}
        onUpdateSuccess={onUpdateSuccess}
        ticketId={`${ticket.id}`}
      />
    </Primitive.div>
  );
};
